/**
 * Bidding rules, including the restriction that makes boerenbridge what it is:
 * the dealer bids last and may not let the bids add up to the number of tricks,
 * so somebody always goes wrong.
 */

import type { HouseRules, ScoreEntry } from '@shared/types/game';

/**
 * The one number the last bidder may not say, or `null` when there is no
 * restriction — either because the house rule is off, or because the earlier
 * bids already overshoot the round so every bid is legal anyway.
 *
 * @param cards      Cards dealt this round, i.e. the number of tricks.
 * @param bidsSoFar  Bids of everyone before the last bidder.
 */
export function forbiddenBidFor(
  cards: number,
  bidsSoFar: readonly number[],
  houseRules: HouseRules
): number | null {
  if (!houseRules.dealerRestriction) return null;

  const placed = bidsSoFar.reduce((sum, bid) => sum + bid, 0);
  const forbidden = cards - placed;

  // Outside 0..cards it is not a biddable number, so nothing is actually barred.
  return forbidden >= 0 && forbidden <= cards ? forbidden : null;
}

/**
 * Bids a player may make. `isLastBidder` is true only for the dealer, who is
 * the one the restriction applies to.
 */
export function legalBids(
  cards: number,
  bidsSoFar: readonly number[],
  isLastBidder: boolean,
  houseRules: HouseRules
): number[] {
  const all = Array.from({ length: cards + 1 }, (_, bid) => bid);
  if (!isLastBidder) return all;

  const forbidden = forbiddenBidFor(cards, bidsSoFar, houseRules);
  return forbidden === null ? all : all.filter((bid) => bid !== forbidden);
}

export function isBidLegal(
  bid: number,
  cards: number,
  bidsSoFar: readonly number[],
  isLastBidder: boolean,
  houseRules: HouseRules
): boolean {
  if (!Number.isInteger(bid) || bid < 0 || bid > cards) return false;
  if (!isLastBidder) return true;
  return forbiddenBidFor(cards, bidsSoFar, houseRules) !== bid;
}

/** Tricks not yet accounted for — drives the "nog X slagen te verdelen" hint. */
export function remainingTricks(cards: number, entries: readonly ScoreEntry[]): number {
  const entered = entries.reduce((sum, entry) => sum + (entry.tricksWon ?? 0), 0);
  return cards - entered;
}

export type TrickSumProblem = 'too-few' | 'too-many' | 'incomplete' | null;

/**
 * Why a round's trick counts cannot be accepted yet, or `null` when they are
 * valid. Exactly `cards` tricks exist, so the entered counts must add up.
 */
export function trickSumProblem(cards: number, entries: readonly ScoreEntry[]): TrickSumProblem {
  if (entries.some((entry) => entry.tricksWon === null)) return 'incomplete';

  const total = entries.reduce((sum, entry) => sum + (entry.tricksWon ?? 0), 0);
  if (total < cards) return 'too-few';
  if (total > cards) return 'too-many';
  return null;
}

/**
 * How far the table's bids are from the number of tricks. Positive means the
 * table is optimistic and at least that many bids must fail; negative means
 * tricks will go to players who did not ask for them.
 */
export function bidPressure(cards: number, entries: readonly ScoreEntry[]): number {
  const total = entries.reduce((sum, entry) => sum + (entry.bid ?? 0), 0);
  return total - cards;
}

/**
 * How far the table's bids are off the number of tricks available.
 *
 * The number every player at the table works out in their head after the last
 * bid, because it says whether the round will be a scramble or a give-away:
 * overbid means somebody must fall short, underbid means tricks are going
 * spare.
 *
 * Lives here rather than in a component because the scorekeeper wants the same
 * number, and two implementations of one sum drift.
 */
export interface BidBalance {
  /** Everything bid so far. */
  total: number;
  /** Positive is overbid, negative underbid, zero exactly shared out. */
  difference: number;
  /** True once nobody is left to bid. */
  complete: boolean;
}

export function bidBalance(cards: number, bids: ReadonlyArray<number | null>): BidBalance {
  const placed = bids.filter((bid): bid is number => bid !== null);
  const total = placed.reduce((sum, bid) => sum + bid, 0);

  return {
    total,
    difference: total - cards,
    complete: placed.length === bids.length && bids.length > 0,
  };
}

/** "2 overgeboden", "1 ondergeboden", "precies verdeeld". */
export function describeBidBalance(balance: BidBalance): string {
  if (balance.difference === 0) return 'precies verdeeld';
  const amount = Math.abs(balance.difference);
  return balance.difference > 0 ? `${amount} overgeboden` : `${amount} ondergeboden`;
}
